import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db8dij8kk00l6eltrj10";

const timeline = [
  {
    year: "2021",
    title: "平台起步",
    desc: "蜜桃传媒正式上线，以影视资讯与作品介绍为起点，开始持续更新娱乐内容。",
  },
  {
    year: "2022",
    title: "内容体系完善",
    desc: "建立电影、电视剧与明星动态的分栏结构，补充筛选与检索能力，让查找更高效。",
  },
  {
    year: "2023",
    title: "专题化探索",
    desc: "推出精选专题栏目，围绕悬疑、治愈、科幻等主题进行内容聚合，帮助用户按兴趣深入浏览。",
  },
  {
    year: "2024",
    title: "体验持续升级",
    desc: "优化阅读体验与移动端适配，完善收藏与分享等实用功能，让浏览更轻松。",
  },
];

const contactInfo = [
  { icon: "ri-mail-line", label: "合作邮箱", value: "hello@peachmedia.example" },
  { icon: "ri-phone-line", label: "联系电话", value: "400-000-0000" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "北京市朝阳区文化创意产业园 A 座" },
  { icon: "ri-time-line", label: "服务时间", value: "周一至周五 09:30 - 18:30" },
];

export default function AboutPage() {
  usePageMeta(
    "关于我们 - 蜜桃传媒",
    "了解蜜桃传媒的网站定位、发展历程与联系方式，欢迎通过反馈表单提交建议与问题。"
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("感谢反馈！你的建议已提交，我们会认真阅读。");
      form.reset();
      return;
    }

    formData.delete("website_alt");
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("感谢反馈！你的建议已提交，我们会认真阅读。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <header className="mt-4 rounded-lg bg-secondary-100 p-6 md:p-10">
        <span className="rounded-full bg-primary-500 px-3 py-1 text-[11px] font-bold text-background-50">
          ABOUT PEACH MEDIA
        </span>
        <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
          关于蜜桃传媒
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-loose text-foreground-700 md:text-base">
          蜜桃传媒定位为专业的影视内容与传媒资讯平台，主要面向喜爱电影、电视剧及娱乐新闻的广大影视爱好者和传媒行业关注者。我们致力于提供清晰的分类、可靠的内容与舒适的阅读体验，满足用户查看影视作品介绍、获取最新娱乐报道、追踪明星动态和热点事件的需求，兼顾内容丰富性与更新时效性。
        </p>
      </header>

      <section className="mt-10" aria-label="网站定位">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-compasses-2-line text-xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-base font-black text-foreground-950">平台定位</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              以影视内容与传媒资讯为核心，为影视爱好者提供一站式的浏览与检索体验。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-user-voice-line text-xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-base font-black text-foreground-950">目标用户</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              喜爱电影、电视剧与娱乐新闻的观众，以及关注传媒行业动态的从业者与爱好者。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-200 text-secondary-900">
              <i className="ri-heart-3-line text-xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-base font-black text-foreground-950">内容理念</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              内容求真、表达克制、阅读友好，用清晰的结构帮助用户高效获取所需信息。
            </p>
          </div>
        </div>
      </section>

      <section className="mt-12" aria-label="发展历程">
        <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
          <i className="ri-route-line text-primary-600"></i>
          发展历程
        </h2>
        <ol className="mt-6 space-y-0">
          {timeline.map((item, index) => (
            <li key={item.year} className="relative flex gap-4 pb-8 last:pb-0">
              <div className="flex flex-col items-center">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-black text-background-50">
                  {index + 1}
                </span>
                {index < timeline.length - 1 && (
                  <span className="mt-1 w-px flex-1 bg-background-300"></span>
                )}
              </div>
              <div className="flex-1 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-bold text-accent-900">
                    {item.year}
                  </span>
                  <h3 className="font-heading text-base font-black text-foreground-950">
                    {item.title}
                  </h3>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2" aria-label="联系方式与反馈">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
            <i className="ri-contacts-line text-primary-600"></i>
            联系方式
          </h2>
          <ul className="mt-5 space-y-3">
            {contactInfo.map((info) => (
              <li
                key={info.label}
                className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900">
                  <i className={`${info.icon} text-xl`}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">{info.label}</p>
                  <p className="text-sm font-medium text-foreground-900">{info.value}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-5 rounded-lg bg-background-100 p-4 text-xs leading-relaxed text-foreground-500">
            本站为演示站点，站内影视作品、资讯与人物信息均为虚拟示例，不代表任何真实作品或个人。
          </div>
        </div>

        <div>
          <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
            <i className="ri-chat-smile-2-line text-primary-600"></i>
            意见反馈
          </h2>
          <form
            id="peach-feedback-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  称呼
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的称呼"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  邮箱
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收回复"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                反馈类型
              </label>
              <select
                id="topic"
                name="topic"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              >
                <option value="内容建议">内容建议</option>
                <option value="功能问题">功能问题</option>
                <option value="内容纠错">内容纠错</option>
                <option value="合作咨询">合作咨询</option>
              </select>
            </div>

            <div className="mt-4">
              <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                留言内容
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请描述你的建议或遇到的问题（最多 500 字）"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              ></textarea>
            </div>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="peach-optional-field"
            />

            <button
              type="submit"
              disabled={status === "submitting"}
              className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "submitting" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  提交中...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  提交反馈
                </>
              )}
            </button>

            {status === "success" && (
              <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm font-medium text-primary-800">
                <i className="ri-checkbox-circle-line text-lg"></i>
                {message}
              </p>
            )}
            {status === "error" && (
              <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm font-medium text-accent-800">
                <i className="ri-error-warning-line text-lg"></i>
                {message}
              </p>
            )}
          </form>

          <div className="mt-5 rounded-lg bg-secondary-100 p-4 text-center">
            <p className="text-sm text-foreground-700">想先逛逛站内内容？</p>
            <div className="mt-3 flex flex-wrap justify-center gap-3">
              <Link
                to="/category/movie"
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-bold text-background-50 hover:bg-primary-600"
              >
                浏览电影
              </Link>
              <Link
                to="/category/tv"
                className="whitespace-nowrap rounded-md border border-secondary-300 bg-background-50 px-4 py-2 text-sm font-bold text-foreground-800 hover:border-primary-300"
              >
                浏览电视剧
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}